import React, { useEffect, useState } from 'react';
import { TreeNode } from './index';
import ReferenceSelectionFormSearch from './ReferenceSelectionFormSearch';
import 'react-virtualized/styles.css';
import 'material-icons/css/material-icons.css';
import ReferenceTreeWrapper from './ReferenceTreeWrapper';
import { Box, Button, ButtonGroup, Stack } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import FormComponent from 'components/common/form/Form';
import pollingService, { PoolingRequestData } from 'tools/pollingService';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import useReferenceHierarchy from './useReferenceHierarchy';

export default function ReferenceSelectionForm({
    profileId,
    referenceId,
    onSelectReference,
    capability,
}: {
    profileId: number;
    referenceId: number;
    onSelectReference: (referenceId: number, collectionId: number) => void;
    capability?: string;
}) {
    const { nodesList, infoRequestData, isInfoRequestSuccess, refetchInfoRequest } = useReferenceHierarchy(
        profileId,
        referenceId,
        capability,
    );

    const [hasRefreshIcon, setHasRefreshIcon] = useState(false);
    const [hasPlusIcon, setHasPlusIcon] = useState(false);
    useEffect(() => {
        setHasRefreshIcon(infoRequestData?.isRefresh ?? false);
        setHasPlusIcon(infoRequestData?.isPlus ?? false);
    }, [infoRequestData]);

    const handleNodeClick = function (node: TreeNode) {
        if (node?.data?.type == 'reference') {
            // Select reference;
            onSelectReference(Number(node.id), Number(node.data.collection_id));
        } else if (node?.data?.linked_ref_id ?? 0 > 0) {
            const refNode = nodesList.find((n) => n.data?.type == 'reference' && n.id == node.data?.linked_ref_id);
            onSelectReference(Number(node.data?.linked_ref_id), Number(refNode?.id ?? 0));
        }
    };

    // Search field
    const handleSearchFilterChange = function (event: any): void {
        setSearchFilterValue(event);
    };
    const [searchFilterValue, setSearchFilterValue] = useState<string>('');

    const [isPooling, setIsPooling] = useState(false);
    const handleRefreshClick = () => {
        const { create } = pollingService<any, PoolingRequestData>({
            onSuccess: () =>
                refetchInfoRequest().then((response) => {
                    if (response.status != 'success') {
                        //@ts-ignore
                        alert(response?.message ?? 'error');
                    }
                    setIsPooling(false);
                }),
            onError: (response) => {
                //@ts-ignore
                alert(response?.message ?? 'error');
                setIsPooling(false);
            },
            requestData: {
                poolingCreateURL: `/data/editor/plugin-source/${profileId}/external-report-list/refresh`,
                poolingCreateMethod: 'GET',
            },
        });
        setIsPooling(true);
        create();
    };

    const [showAddPopup, setShowAddPopup] = React.useState<boolean>(false);
    const afterAdd = (response?: any) => {
        handleRefreshClick();
    };

    return (
        <>
            {(isPooling || !isInfoRequestSuccess) && <LoadingPlaceholder />}
            <Stack direction={'row'}>
                <Box flexGrow={1}>
                    <ReferenceSelectionFormSearch onChange={handleSearchFilterChange} value={searchFilterValue} />
                </Box>
                {(hasPlusIcon || hasRefreshIcon) && (
                    <Box sx={{ ml: 1, flexShrink: 0 }}>
                        <ButtonGroup aria-label="outlined primary button group">
                            {hasPlusIcon && (
                                <Button onClick={() => setShowAddPopup(true)}>
                                    <IconMi icon={'new'} fontSize="16" />
                                </Button>
                            )}
                            {hasRefreshIcon && (
                                <Button onClick={handleRefreshClick}>
                                    <IconMi icon={'refresh-cw'} fontSize="16" />
                                </Button>
                            )}
                        </ButtonGroup>
                    </Box>
                )}
            </Stack>

            <ReferenceTreeWrapper
                onNodeClick={handleNodeClick}
                pattern={searchFilterValue}
                nodes={nodesList}
                referenceId={referenceId}
            />

            {showAddPopup && (
                <FormComponent
                    popupSettings={{}}
                    isPopup={true}
                    popupType={'new'}
                    pk={''}
                    uid={'reference-selection-add-form'}
                    settingsUrl={`/data/editor/external-report-reference-on-fly/${profileId}/form`}
                    onHide={() => setShowAddPopup(false)}
                    afterSave={afterAdd}
                />
            )}
        </>
    );
}
